import { useMemo } from "react";

import { useHistory } from "react-router";

import { Button, Icon, Text, toastError, toastSuccess } from "@alpacahq/ui/old";

import {
  useGetAllRelationships,
  useGetAllTransfers,
  useGetIsLegalEntity,
  useGetOwnerDetails,
} from "~/v2/api/hooks";
import { FullScreenSpinner } from "~/v2/components/common";
import { Path } from "~/v2/path";
import { useAccountContext } from "~/v2/providers/AccountProvider";
import { FAQ } from "../../../../components/common/FAQ";
import { GENERAL_FAQ } from "..";
import { Layout } from "../common/Layout";
import { TransferTypeButton } from "../common/TransferTypeButton";
import {
  getLastTransferType,
  isACHPendingMicroDeposit,
  useIsACHOptionAllowed,
} from "../common/util";
import { useUnlinkRelationship } from "../LinkedAccounts/useUnlinkRelationship";

const CRYPTO_ACH_WARNING_LINK =
  "https://alpaca.markets/support/why-are-my-crypto-withdrawal-requests-failing";

const Deposit = () => {
  const history = useHistory();
  const { currentAccount } = useAccountContext({ liveOnly: true });
  const { isLegalEntity } = useGetIsLegalEntity();
  const { details } = useGetOwnerDetails();

  const { ach, wire, currencyCloud } = useGetAllRelationships({
    account: currentAccount,
    countryOfTaxResidence: details?.country_of_tax_residence || "",
  });

  // Find the first active (non-canceled, non-rejected) relationship for use
  const activeAchRelationship = ach.relationships.find(
    (rel) => rel.status !== "CANCELED" && rel.status !== "REJECTED",
  );

  // Also check if there's a rejected relationship that needs to be handled
  const rejectedAchRelationship = ach.relationships.find(
    (rel) => rel.status === "REJECTED",
  );

  // Use active relationship if available, otherwise show rejected for unlinking
  const achRelationship = activeAchRelationship || rejectedAchRelationship;

  const isACHMicroDepositVerificationRequired = isACHPendingMicroDeposit(
    activeAchRelationship,
  );

  // to only highlight us or international wire button based on the last transfer
  const wireRelationship = wire.relationships.at(0);
  const isWireRelationshipInternational =
    !!wireRelationship &&
    wireRelationship.country !== "US" &&
    wireRelationship.country !== "USA" &&
    wireRelationship.country !== "";

  const transfers = useGetAllTransfers({
    accountID: currentAccount?.id || "",
    isCurrencyCloudEnabled: currencyCloud.isEnabled,
  });
  const lastTransferType = useMemo(
    () => getLastTransferType(transfers, "INCOMING"),
    [transfers],
  );

  const {
    unlinkModal,
    unlinkRelationship,
    isLoading: isUnlinkLoading,
  } = useUnlinkRelationship({
    accountID: currentAccount?.id ?? "",
    onSuccess: (relationship) => {
      if (relationship?.type === "ACH") {
        ach.refetch();
      } else if (relationship?.type === "Wire") {
        wire.refetch();
      }
      toastSuccess(`${relationship.type} connection removed`);
    },
    onError: () => {
      toastError(`An error occurred while removing the bank connection.`);
    },
  });

  const onACHClicked = () => {
    // If no active relationship or micro deposit verification is required, go to link page
    if (!activeAchRelationship || isACHMicroDepositVerificationRequired) {
      history.push({
        pathname: Path.ROUTE_FUNDING_LINK_ACH,
        search: `?transfer=deposit${
          isACHMicroDepositVerificationRequired
            ? `&micro_deposit_verification_required=true&relationship_id=${
                activeAchRelationship?.id ?? ""
              }`
            : ""
        }`,
      });
      return;
    }

    // If there's a rejected relationship, offer to unlink it
    if (rejectedAchRelationship) {
      unlinkRelationship({ ...rejectedAchRelationship, type: "ACH" });
      return;
    }

    history.push(Path.ROUTE_FUNDING_DEPOSIT_ACH);
  };

  const onWireClicked = (isInternational: boolean) =>
    history.push({
      pathname: Path.ROUTE_FUNDING_DEPOSIT_WIRE,
      search: isInternational ? "?international=true" : undefined,
    });

  const onCurrencyCloudClicked = () => {
    if (!currencyCloud.account) {
      history.push({
        pathname: Path.ROUTE_FUNDING_LINK_CURRENCYCLOUD,
        search: "?transfer=deposit",
      });
      return;
    }

    history.push(Path.ROUTE_FUNDING_DEPOSIT_CURRENCYCLOUD);
  };

  const onBackClicked = () => history.push(Path.ROUTE_FUNDING);

  const isACHOptionAllowed = useIsACHOptionAllowed({
    isLegalEntity,
    hasACHRelationship: !!achRelationship,
  });

  // IRA accounts cannot deposit via wire or CurrencyCloud
  const isIRAAccount = currentAccount?.account_type === "ira";

  const isCurrencyCloudOptionAllowed = currencyCloud.isEnabled;

  const isCryptoActive = currentAccount?.crypto_status === "ACTIVE";

  // Edge case - no options to display
  const hasNoOptions = !isACHOptionAllowed && isIRAAccount;

  if (!currentAccount || !details || ach.isLoading || currencyCloud.isLoading) {
    return <FullScreenSpinner />;
  }

  return (
    <Layout
      title="Deposit Funds"
      subtitle="Deposit funds into your Alpaca account."
      faq={<FAQ faq={GENERAL_FAQ} />}
    >
      <Button variant="secondary" onClick={onBackClicked} className="mb-8">
        <Icon name="ArrowLeftCircle" className="mr-2" />
        Back
      </Button>

      {hasNoOptions ? (
        <div className="flex flex-col gap-4">
          <Text className="pb-4 font-semibold text-xl">
            Please contact support at{" "}
            <a href="mailto:support@alpaca.markets">support@alpaca.markets</a>{" "}
            to deposit funds into your account.
          </Text>
        </div>
      ) : (
        <div className="pb-10">
          <Text className="pb-4 font-semibold text-xl">
            Transfer from US bank accounts:
          </Text>

          <div className="flex flex-col gap-4">
            {isACHOptionAllowed && (
              <TransferTypeButton
                title="Direct Bank Transfer (ACH)"
                isRecommended={achRelationship?.status !== "REJECTED"}
                isHighlighted={lastTransferType === "ACH"}
                data={{
                  estimated_arrival: "1 - 3 business days",
                  fees: "Free",
                  deposit_limit: "$50,000",
                  supported_banks: "US Domestic Banks",
                }}
                onClick={onACHClicked}
                linkedAccount={
                  achRelationship
                    ? {
                        account: achRelationship.account_name ?? "",
                        name: achRelationship.brand_name,
                        imgSrc: achRelationship.logo,
                        status:
                          achRelationship.status === "REJECTED"
                            ? { text: "Action Required", variant: "rose" }
                            : undefined,
                      }
                    : undefined
                }
                isDisabled={isUnlinkLoading}
                topBanner={
                  isCryptoActive ? (
                    <div className="mx-4 mb-5 flex items-center justify-between gap-4 rounded border border-orange-600 bg-[rgba(254,215,170,0.24)] p-3">
                      <p className="m-0 p-0 text-gray-800 text-xs">
                        Any ACH deposit will place a 60 day hold on all crypto
                        withdrawals.
                      </p>
                      <a
                        href={CRYPTO_ACH_WARNING_LINK}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="m-0 flex items-center justify-end whitespace-nowrap p-0 text-right font-medium text-indigo-600 text-xs underline"
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      >
                        Read More
                      </a>
                    </div>
                  ) : undefined
                }
              />
            )}

            {!isIRAAccount && (
              <TransferTypeButton
                title="Domestic Wire Transfer"
                isHighlighted={
                  lastTransferType === "Wire" &&
                  !isWireRelationshipInternational
                }
                data={{
                  estimated_arrival: "2 - 3 business days",
                  fees: "Free",
                  deposit_limit: "None",
                  supported_banks: "US Domestic Banks",
                }}
                onClick={() => onWireClicked(false)}
                isDisabled={isUnlinkLoading}
              />
            )}
          </div>

          {!isIRAAccount && (
            <>
              <Text className="pt-10 pb-4 font-semibold text-xl">
                Transfer from international bank accounts:
              </Text>

              <div className="flex flex-col gap-4">
                {isCurrencyCloudOptionAllowed && (
                  <TransferTypeButton
                    title="Local Currency Transfer"
                    isRecommended
                    isHighlighted={lastTransferType === "CurrencyCloud"}
                    data={{
                      estimated_arrival: "1 - 3 business days",
                      fees: "1.5% Fee (Max at $40)",
                      deposit_limit: "None",
                      supported_banks: "Non-US Banks",
                    }}
                    onClick={onCurrencyCloudClicked}
                    linkedAccount={
                      currencyCloud.account
                        ? {
                            account: currencyCloud.account.account_number,
                            name: currencyCloud.account.bank_name,
                          }
                        : undefined
                    }
                    isDisabled={isUnlinkLoading}
                  />
                )}

                <TransferTypeButton
                  title="International Wire Transfer"
                  isHighlighted={
                    lastTransferType === "Wire" &&
                    isWireRelationshipInternational
                  }
                  data={{
                    estimated_arrival: "5 - 7 business Days",
                    fees: "Free",
                    deposit_limit: "None",
                    supported_banks: "Non-US Banks",
                  }}
                  onClick={() => onWireClicked(true)}
                  isDisabled={isUnlinkLoading}
                />
              </div>
            </>
          )}
        </div>
      )}
      {unlinkModal}
    </Layout>
  );
};

export default Deposit;
